// Stencil
import {
  Component,
  Host,
  h,
  Prop,
  State,
  Watch,
  Element,
  getAssetPath
} from "@stencil/core";
// Other Libraries
import { MercuryBundles } from "@genexus/mercury";
import { ComboBoxItemModel } from "@genexus/chameleon-controls-library/dist/types/components/combo-box/types";
// Custom Imports
import { Locale } from "../../common/locale";
import { mapTeamsToComboBoxItemModel } from "./helpers";

const COPYRIGHTS_ILLUSTRATION = getAssetPath(
  `./gx-ide-assets/welcome-page/images/copyrights-illustration.png`
);
const ICON_NEXT = getAssetPath(
  `./gx-ide-assets/welcome-page/images/icon-next.svg`
);
const CSS_BUNDLES: MercuryBundles = [
  "resets/box-sizing",
  "utils/typography",
  "components/button",
  "components/combo-box",
  "components/checkbox",
  "utils/form"
];

const CHECKED_VALUE = "on";

@Component({
  tag: "gx-ide-sign-in-team",
  styleUrl: "sign-in-team.scss",
  shadow: true,
  assetsDirs: ["gx-ide-assets/sign-in-team"]
})
export class GxIdeSignInTeam {
  /**
   * The component hard-coded strings translations.
   */
  // eslint-disable-next-line @stencil-community/own-props-must-be-private
  #componentLocale: any;
  // Select User Team References :
  #userTeamsModel: ComboBoxItemModel[] = [];
  #chSelectTeamEl: HTMLChComboBoxRenderElement;
  #switchStepTransitionTime: number = 100;
  #termsAndConditionsCheckboxEl!: HTMLChCheckboxElement;
  #privacyPolicyCheckboxEl!: HTMLChCheckboxElement;

  @Element() el: HTMLGxIdeSignInTeamElement;

  /**
   * This is a handover from "authStep" that allows updating descriptions once
   * the slide is hidden. This enables a smooth transition between descriptions.
   */

  @State() authStepInternal: AuthStep;

  /**
   * Defines if the main slide is visible or not. Used for smooth transitions
   * between slides.
   */
  @State() slideIsVisible: boolean = true;

  /**
   * True if terms and conditions are accepted.
   */
  @State() termsAndConditionsAccepted: boolean = false;

  /**
   * The authentication step to display
   */
  @Prop() readonly authStep: AuthStep = "signIn";
  @Watch("authStep")
  authStepChanged(newAuthStep: AuthStep) {
    this.slideIsVisible = false;
    // switch step
    setTimeout(() => {
      this.authStepInternal = newAuthStep;
    }, this.#switchStepTransitionTime);
    // show slide
    setTimeout(() => {
      this.slideIsVisible = true;
    }, this.#switchStepTransitionTime * 2);
  }

  /**
   * Callback executed when the user clicks the 'Continue' button
   */
  @Prop() readonly continueCallback!: (teamId: string) => Promise<void>;

  /**
   * Callback executed when the user click the "Sign In" button
   */
  @Prop() readonly signInCallback!: () => Promise<void>;

  /**
   * When true, it will diplay checkboxes for the user to confirm terms and conditions
   */
  @Prop() readonly termsConfirmationRequired: boolean = false;

  /**
   * Array that contains the user's teams
   */
  @Prop() readonly teams!: TeamData[];
  @Watch("teams")
  teamsChanged(newTeams: TeamData[]) {
    this.#userTeamsModel = mapTeamsToComboBoxItemModel(newTeams);
  }

  /**
   * Callback executed when the user clicks on the "term and conditions" link
   */
  @Prop() readonly termsCallback: () => Promise<void>;

  /**
   * Callback executed when the user clicks on the "privacy policy" link
   */
  @Prop() readonly privacyCallback: () => Promise<void>;

  async componentWillLoad() {
    this.#componentLocale = await Locale.getComponentStrings(this.el);
    this.el.style.setProperty(
      "--switch-step-transtion-time",
      this.#switchStepTransitionTime.toString() + "ms"
    );
    // Initialize values
    this.authStepInternal = this.authStep;
    this.teamsChanged(this.teams);
  }

  #authStepCallback = () => {
    if (this.authStep === "signIn") {
      this.signInCallback();
    } else if (this.authStep === "selectUserTeam") {
      this.#continueHandler();
    }
  };

  #continueHandler = async () => {
    await this.continueCallback(this.#chSelectTeamEl.value);
  };

  #renderStepHeader = (): HTMLElement => {
    return (
      <header class="slide__main-header">
        <img
          src={ICON_NEXT}
          alt="" // decorative
        />
        <h2
          // Split the title into two spans for better styling
          class="slide__main-title heading-2"
        >
          <span>{this.#componentLocale[this.authStepInternal].titlePart1}</span>
          <span>{this.#componentLocale[this.authStepInternal].titlePart2}</span>
        </h2>
      </header>
    );
  };

  #renderStepFooter = (): HTMLElement => {
    const signInDisabled =
      this.authStep === "signIn" &&
      this.termsConfirmationRequired &&
      !this.termsAndConditionsAccepted;
    return (
      <div>
        <button
          class="button-primary main-btn"
          onClick={this.#authStepCallback}
          disabled={signInDisabled}
        >
          {this.#componentLocale[this.authStepInternal].buttonCaption}
        </button>
      </div>
    );
  };

  #evaluateTermsConditionsAndPrivacyAccepted = () => {
    const termsAndConditionsAccepted =
      this.#termsAndConditionsCheckboxEl.value === CHECKED_VALUE;
    const privacyPolicyAccepted =
      this.#privacyPolicyCheckboxEl.value === CHECKED_VALUE;
    this.termsAndConditionsAccepted =
      termsAndConditionsAccepted && privacyPolicyAccepted;
  };

  #termsAndConditionsClickedHandler = (event: MouseEvent) => {
    event.preventDefault();
    this.termsCallback();
  };

  #privacyPolicyClickedHandler = (event: MouseEvent) => {
    event.preventDefault();
    this.privacyCallback();
  };

  #evaluateStepRender = () => {
    if (this.authStepInternal === "signIn") {
      return [
        this.#renderStepHeader(),
        <p class="body-regular-l">
          {this.#componentLocale.signIn.description}
        </p>,
        this.termsConfirmationRequired && (
          <div class="field-group">
            <div
              // terms and conditions
              class="field field-inline"
            >
              <ch-checkbox
                checkedValue={CHECKED_VALUE}
                id="terms-and-conditions"
                class="checkbox"
                ref={(el: HTMLChCheckboxElement) =>
                  (this.#termsAndConditionsCheckboxEl =
                    el as HTMLChCheckboxElement)
                }
                onInput={this.#evaluateTermsConditionsAndPrivacyAccepted}
              ></ch-checkbox>
              <span>
                {/*
                  WA: this span prevents field-inline applying padding-block-start on the label
                  TODO: See how to ignore padding-block-start when using field-inline with a control
                  that is not an input, combo-box, etc..
                  */}
                <label
                  htmlFor="terms-and-conditions"
                  class="label label-agreement"
                >
                  {this.#componentLocale.agreement.termsAndConditionsLabel}
                  <span
                    class={{ hyperlink: !!this.termsCallback }}
                    onClick={
                      this.termsCallback &&
                      this.#termsAndConditionsClickedHandler
                    }
                  >
                    {this.#componentLocale.agreement.termsAndConditionsLink}
                  </span>
                </label>
              </span>
            </div>
            <div
              // privacy policy
              class="field field-inline"
            >
              <ch-checkbox
                checkedValue={CHECKED_VALUE}
                id="privacy-policy"
                class="checkbox"
                ref={(el: HTMLChCheckboxElement) =>
                  (this.#privacyPolicyCheckboxEl = el as HTMLChCheckboxElement)
                }
                onInput={this.#evaluateTermsConditionsAndPrivacyAccepted}
              ></ch-checkbox>
              <span>
                {/*
                  WA: this span prevents field-inline applying padding-block-start on the label
                  TODO: See how to ignore padding-block-start when using field-inline with a control
                  that is not an input, combo-box, etc..
                  */}
                <label htmlFor="privacy-policy" class="label label-agreement">
                  {this.#componentLocale.agreement.privacyAndPolicyLabel}
                  <span
                    class={{ hyperlink: !!this.privacyCallback }}
                    onClick={
                      this.privacyCallback && this.#privacyPolicyClickedHandler
                    }
                  >
                    {this.#componentLocale.agreement.privacyAndPolicyLink}
                  </span>
                </label>
              </span>
            </div>
          </div>
        ),

        this.#renderStepFooter()
      ];
    } else if (
      this.authStepInternal === "fetchUserTeams" ||
      this.authStepInternal === "settingUpSession"
    ) {
      const loaderTitle =
        this.authStepInternal === "fetchUserTeams"
          ? this.#componentLocale.loader.fetchingTeamsTitle
          : this.#componentLocale.loader.settingUpSessionTitle;
      const loaderDescription =
        this.authStepInternal === "fetchUserTeams"
          ? this.#componentLocale.loader.fetchingTeamsDescription
          : this.#componentLocale.loader.settingUpDescription;
      return (
        <gx-ide-loader
          show
          loaderTitle={loaderTitle}
          description={loaderDescription}
        ></gx-ide-loader>
      );
    } else if (this.authStepInternal === "selectUserTeam") {
      return [
        this.#renderStepHeader(),
        <div class="slide__select-team-description">
          <p class="body-semi-bold-l">
            {this.#componentLocale.selectUserTeam.infoMessage}
          </p>
        </div>,
        <div class="field-group">
          <div class="field field-block">
            <label htmlFor="teams">
              {this.#componentLocale.selectUserTeam.label}
            </label>
            <ch-combo-box-render
              id="teams"
              accessibleName="Teams"
              class="combo-box teams-list"
              model={this.#userTeamsModel}
              value={this.#userTeamsModel[0]?.value}
              ref={(el: HTMLChComboBoxRenderElement) =>
                (this.#chSelectTeamEl = el as HTMLChComboBoxRenderElement)
              }
            ></ch-combo-box-render>
          </div>
        </div>,
        this.#renderStepFooter()
      ];
    }
  };

  render() {
    return (
      <Host>
        <ch-theme model={CSS_BUNDLES}></ch-theme>
        <section class="slide">
          <img
            src={COPYRIGHTS_ILLUSTRATION}
            class="slide__illustration"
            alt="" // decorative
          />

          <div
            class={{
              "slide__main": true,
              "slide__main--visible": this.slideIsVisible
            }}
          >
            <div class="slide__main-container">
              {this.#evaluateStepRender()}
            </div>
          </div>
        </section>
      </Host>
    );
  }
}

export type AuthStep =
  | "signIn"
  | "fetchUserTeams"
  | "selectUserTeam"
  | "settingUpSession";

export type TeamData = {
  id: string;
  name: string;
};
